/* eslint no-unused-vars:off */
import { ProductStatusType } from "@/apps/dashboard/catalog/core/infrastructure/service/productsService";

export type SearchOrder = "asc" | "desc";
export type PriceFilterOptions = { min: number | null; max: number | null };

export type SearchFilterOptions = {
  sort: {
    field: string;
    order: SearchOrder;
  };
  page: number;
  itemsPerPage: number;
};

export type ProductFilterOptions = {
  label: string;
  price: PriceFilterOptions;
  categories: string[];
  status: ProductStatusType;
  page: number;
  itemsPerPage: number;
};

export type ProductSearchFilterOptions = ProductFilterOptions & SearchFilterOptions;

const getDefaultFilters = (): ProductSearchFilterOptions => ({
  categories: [],
  price: { min: null, max: null },
  label: "",
  status: ProductStatusType.PUBLISHED,
  sort: {
    field: "label",
    order: "asc",
  },
  page: 1,
  itemsPerPage: 10,
});

export const saveFilters = (options: ProductSearchFilterOptions): void => {
  localStorage.setItem("products-filters", JSON.stringify(options));

  setUrlQueryParams(options);
};

export const getFilters = (): ProductSearchFilterOptions => {
  const urlFilters = getFiltersFromUrl();
  if (urlFilters) {
    return urlFilters;
  }

  const storedFilters = JSON.parse(localStorage.getItem("products-filters") as string);
  if (storedFilters) {
    return storedFilters;
  }

  return getDefaultFilters();
};

const getFiltersFromUrl = (): ProductSearchFilterOptions | null => {
  const searchUrlArray = Array.from(new URLSearchParams(window.location.search).entries());
  if (searchUrlArray.length === 0) {
    return null;
  }

  const filters = getDefaultFilters();
  searchUrlArray.forEach(([key, value]) => {
    switch (key) {
      case "categories":
        filters.categories = value.split(",");
        break;
      case "order[field]":
        filters.sort.field = key.match(/\[(.*?)\]/)![1]!;
        filters.sort.order = <SearchOrder>value;
        break;
      case "order[order]":
        filters.sort.field = key.match(/\[(.*?)\]/)![1]!;
        filters.sort.order = <SearchOrder>value;
        break;
      case "buybackPriceGradeA[gte]":
        filters.price.min = Number(value);
        break;
      case "buybackPriceGradeA[lte]":
        filters.price.max = Number(value);
        break;
      case "label":
        filters.label = value;
        break;
      case "status":
        filters.status = value as ProductStatusType;
        break;
      case "page":
        filters.page = Number(value);
        break;
      case "itemsPerPage":
        filters.itemsPerPage = Number(value);
        break;
      default:
        break;
    }
  });

  return filters;
};

const setUrlQueryParams = (options: ProductSearchFilterOptions): void => {
  const url = new URL(window.location.href);
  url.search = String(mapFiltersToUrl(options));

  window.history.pushState({}, "", String(url));
};

const mapFiltersToUrl = (options: ProductSearchFilterOptions): URLSearchParams => {
  const min = options.price ? options.price.min : null;
  const max = options.price ? options.price.max : null;
  const field = options.sort ? options.sort.field : null;
  const order = options.sort ? options.sort.order : null;

  const searchFilters = Object.entries({
    label: options.label,
    categories: options.categories.length > 0 ? options.categories.join(",") : null,
    status: options.status,
    "buybackPriceGradeA[gte]": min,
    "buybackPriceGradeA[lte]": max,
    [`order[${field}]`]: order,
    page: options.page,
    itemsPerPage: options.itemsPerPage,
  })
    .filter(([, value]) => !!value)
    .map(([key, value]) => [key, String(value)]);

  return new URLSearchParams(Object.fromEntries(searchFilters));
};
